import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Zap, Mic, ArrowRight, CheckCircle2, Calculator, ChevronRight, ShieldCheck, WifiOff, RefreshCw, Send,
  Activity, Lock, CalendarDays, Hammer, Wallet, Users, Handshake,
} from "lucide-react";
import { DictationMock, PlanningMock, KanbanMock, TreasuryMock, CaptureMock } from "@/components/produit/Mockups";

const REGISTER_URL = "/login?register=1";
const PARTNER_MAILTO = "mailto:contact@kallitag.fr?subject=Demande%20de%20d%C3%A9mo%20partenaire%20Kallitag";
const NAVY = "#0F172A";

const FEATURES = [
  {
    id: "voix", kicker: "01 · Dictée IA", icon: Mic, mock: DictationMock, dark: false,
    title: "Une phrase. Quatre objets créés.",
    desc: "Appuyez sur le micro central et racontez votre chantier comme à un collègue. L'IA extrait en une seule passe le client, le devis détaillé, le créneau au planning et l'échéancier acompte / solde. Vous vérifiez, vous validez — c'est tout.",
    points: ["Onde vocale réactive en temps réel", "Écran de validation avant enregistrement", "Acompte en % calculé automatiquement"],
  },
  {
    id: "planning", kicker: "02 · Planning", icon: CalendarDays, mock: PlanningMock, dark: false,
    title: "Vos chantiers sur plusieurs jours, d'un coup d'œil.",
    desc: "Les interventions longues s'affichent en bandes continues sur la semaine. Plus de rendez-vous éclatés : vous voyez immédiatement qui est où, et quand.",
    points: ["Bandes multi-jours continues", "Statuts Planifié · Terminé · Encaissé", "Rappels automatiques la veille"],
  },
  {
    id: "devis", kicker: "03 · Devis → Chantier", icon: Hammer, mock: KanbanMock, dark: false,
    title: "Signé ? C'est planifié et dans votre prévisionnel.",
    desc: "Un tableau Kanban du brouillon à l'encaissement. Dès qu'un devis passe en « Signé », il devient un chantier planifié et son montant bascule dans votre CA prévisionnel. Sur le terrain, un simple glissement de doigt suffit.",
    points: ["Brouillon → Envoyé → Signé → En cours → Terminé → Encaissé", "Swipe → pour encaisser, ← pour terminer ou noter", "Liste par statut optimisée mobile"],
  },
  {
    id: "tresorerie", kicker: "04 · Trésorerie", icon: Wallet, mock: TreasuryMock, dark: true,
    title: "Votre trésorerie, enfin lisible.",
    desc: "Un pilotage façon néobanque : le CA encaissé repose sur les dates de paiement réelles, le CA prévisionnel sur les échéances attendues. Chaque carte s'ouvre sur un registre détaillé, mois par mois, avec récap annuel et exports.",
    points: ["Encaissé vs prévisionnel, sans approximation", "Registres cliquables + export CSV / PDF", "Alertes sur les échéances en retard"],
  },
  {
    id: "capture", kicker: "05 · Clients & leads", icon: Users, mock: CaptureMock, dark: false,
    title: "Chaque contact capturé, même hors réseau.",
    desc: "Carte de visite scannée par IA, carte NFC Kallitag ou QR code inversé : la fiche client se remplit seule, puis se synchronise vers votre CRM.",
    points: ["OCR IA, NFC et QR inversé", "Mode hors-ligne avec synchro auto", "HubSpot, Salesforce, Pipedrive"],
  },
];

const EXTRAS = [
  { icon: WifiOff, t: "Offline-first", d: "Fonctionne sans réseau, synchronise au retour." },
  { icon: Send, t: "Relances auto", d: "Séquences email J+1, J+3, J+7 sur vos prospects." },
  { icon: Activity, t: "Score IA", d: "Probabilité de signature et température du lead." },
  { icon: RefreshCw, t: "Sync CRM", d: "Connecteurs OAuth natifs en un clic." },
  { icon: Lock, t: "RGPD", d: "Consentement horodaté, données hébergées en UE." },
  { icon: ShieldCheck, t: "Rôles & équipes", d: "Espaces Manager et Commercial, multi-sociétés." },
];

const PLANS = [
  { name: "Solo", price: "24,90 €", unit: "/mois", sub: "1 utilisateur" },
  { name: "Équipe", price: "21,90 €", unit: "/licence/mois", sub: "2 à 9 licences", popular: true },
  { name: "Entreprise", price: "19,90 €", unit: "/licence/mois", sub: "10 licences et +" },
];

const fmt = new Intl.NumberFormat("fr-FR");
const fmtEur = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

function CtaButton({ testid, children, light = false, to = REGISTER_URL }) {
  return (
    <Link to={to} data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full font-bold text-base transition-transform hover:-translate-y-0.5 active:scale-[0.98] ${light ? "bg-white text-[#0F172A] hover:bg-slate-100" : "bg-[#10B981] text-white shadow-xl shadow-emerald-600/30 hover:bg-emerald-500"}`}>
      {children}
    </Link>
  );
}

function Header() {
  return (
    <header className="sticky top-0 z-40 bg-[#0F172A]/90 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="produit-logo">
          <div className="w-9 h-9 rounded-xl bg-[#10B981] flex items-center justify-center"><Zap className="w-5 h-5 text-white" fill="currentColor" /></div>
          <div className="leading-tight text-white"><div className="font-heading font-extrabold">Kallitag</div><div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400">Terrain</div></div>
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm text-slate-300">
          {FEATURES.map((f) => <a key={f.id} href={`#${f.id}`} data-testid={`produit-nav-${f.id}`} className="hover:text-white transition-colors">{f.kicker.split("· ")[1]}</a>)}
          <a href="#tarifs" data-testid="produit-nav-tarifs" className="hover:text-white transition-colors">Tarifs</a>
        </nav>
        <Link to="/login" data-testid="produit-header-login" className="text-sm font-semibold text-white border border-white/20 rounded-full px-4 py-2 hover:bg-white/10 transition-colors">Se connecter</Link>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#0F172A] text-white">
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)", backgroundSize: "48px 48px" }} />
      <div className="absolute -bottom-40 -left-20 w-[34rem] h-[34rem] bg-[#10B981]/20 blur-3xl rounded-full" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
        <div>
          <span className="produit-rise inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-full px-3 py-1"><Mic className="w-3.5 h-3.5" /> L'outil terrain des artisans & commerciaux</span>
          <h1 className="produit-rise font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-6 leading-[1.04]" style={{ animationDelay: ".1s" }}>
            Dictez votre chantier.<br /><span className="text-[#10B981]">L'appli fait le reste.</span>
          </h1>
          <p className="produit-rise text-base sm:text-lg text-slate-300 mt-6 max-w-xl leading-relaxed" style={{ animationDelay: ".2s" }}>
            Client, devis, planning et trésorerie créés en une seule dictée. Du premier contact à l'encaissement, tout tient dans votre poche — même sans réseau.
          </p>
          <div className="produit-rise mt-9 flex flex-col sm:flex-row gap-3" style={{ animationDelay: ".3s" }}>
            <CtaButton testid="produit-hero-cta">Démarrer l'essai gratuit (7 jours) <ArrowRight className="w-5 h-5" /></CtaButton>
            <a href="#voix" data-testid="produit-hero-demo" className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full border border-white/20 font-semibold hover:bg-white/10 transition-colors">Voir comment ça marche</a>
          </div>
          <p className="produit-rise text-sm text-slate-400 mt-4 flex items-center gap-1.5" style={{ animationDelay: ".35s" }}><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> Sans carte bancaire · Prêt en 1 minute</p>
        </div>
        <div className="relative" data-testid="produit-hero-mock"><DictationMock /></div>
      </div>
    </section>
  );
}

function Stats() {
  const s = [["1 dictée", "= client + devis + planning + échéancier"], ["5 onglets", "Planning · Devis · IA · Trésorerie · Clients"], ["0 €", "de CA oublié : tout est rapproché"], ["100 %", "utilisable hors réseau"]];
  return (
    <section className="bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
        {s.map(([a, b], i) => (
          <div key={i} data-testid={`produit-stat-${i}`} className="border-l-2 border-[#10B981] pl-4">
            <div className="font-heading text-2xl sm:text-3xl font-extrabold text-[#0F172A]">{a}</div>
            <div className="text-sm text-slate-500 mt-1">{b}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FeatureBlock({ f, idx }) {
  const Icon = f.icon;
  const Mock = f.mock;
  const reverse = idx % 2 === 1;
  return (
    <section id={f.id} data-testid={`produit-pillar-${f.id}`} className={`${f.dark ? "bg-[#0F172A] text-white" : idx % 2 ? "bg-slate-50" : "bg-white"} scroll-mt-16`}>
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center`}>
        <div className={reverse ? "lg:order-2" : ""}>
          <div className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${f.dark ? "text-emerald-300" : "text-[#10B981]"}`}><Icon className="w-4 h-4" /> {f.kicker}</div>
          <h2 className={`font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-4 leading-tight ${f.dark ? "" : "text-[#0F172A]"}`}>{f.title}</h2>
          <p className={`mt-5 text-base leading-relaxed max-w-lg ${f.dark ? "text-slate-300" : "text-slate-600"}`}>{f.desc}</p>
          <ul className="mt-7 space-y-3">
            {f.points.map((p, i) => (
              <li key={i} data-testid={`produit-feature-${f.id}-${i}`} className={`flex items-start gap-3 text-sm font-medium ${f.dark ? "text-slate-200" : "text-slate-800"}`}>
                <CheckCircle2 className="w-5 h-5 text-[#10B981] shrink-0" /> {p}
              </li>
            ))}
          </ul>
        </div>
        <div className={reverse ? "lg:order-1" : ""}><Mock /></div>
      </div>
    </section>
  );
}

function Extras() {
  return (
    <section className="bg-white border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] max-w-xl">Et tout ce qu'il faut pour travailler sereinement.</h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
          {EXTRAS.map((e, i) => {
            const Icon = e.icon;
            return (
              <div key={i} data-testid={`produit-extra-${i}`} className="flex gap-4 group">
                <div className="w-11 h-11 rounded-xl bg-slate-100 group-hover:bg-[#0F172A] flex items-center justify-center shrink-0 transition-colors"><Icon className="w-5 h-5 text-[#0F172A] group-hover:text-[#10B981] transition-colors" /></div>
                <div><div className="font-semibold text-[#0F172A]">{e.t}</div><div className="text-sm text-slate-500 mt-0.5">{e.d}</div></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function RoiField({ label, testid, value, onChange }) {
  return (
    <label className="block">
      <span className="text-sm text-slate-600">{label}</span>
      <input type="number" min="0" data-testid={testid} value={value} onChange={onChange}
        className="mt-1.5 w-full h-11 rounded-xl bg-white border border-slate-300 px-3 text-[#0F172A] font-semibold focus:border-[#10B981] focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
    </label>
  );
}

function Roi() {
  const [r, setR] = useState({ chantiers: 8, panier: 3500, oublis: 10, heures: 6 });
  const set = (k) => (e) => setR((p) => ({ ...p, [k]: Math.max(0, Number(e.target.value) || 0) }));
  const recovered = Math.round((r.chantiers * r.panier * r.oublis) / 100) * 12;
  const hours = r.heures * 52;
  return (
    <section id="roi" className="bg-slate-50 border-t border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#10B981]"><Calculator className="w-4 h-4" /> Simulateur</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] mt-4">Combien vous rapporte Kallitag ?</h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="produit-roi-inputs">
            <RoiField label="Chantiers / mois" testid="roi-chantiers" value={r.chantiers} onChange={set("chantiers")} />
            <RoiField label="Panier moyen (€)" testid="roi-panier" value={r.panier} onChange={set("panier")} />
            <RoiField label="Devis / relances oubliés (%)" testid="roi-oublis" value={r.oublis} onChange={set("oublis")} />
            <RoiField label="Heures d'admin / semaine" testid="roi-heures" value={r.heures} onChange={set("heures")} />
          </div>
        </div>
        <div className="rounded-3xl bg-[#0F172A] text-white p-8 sm:p-10" data-testid="produit-roi-results">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">CA récupéré par an</div>
          <div className="font-heading text-5xl font-extrabold text-[#10B981] mt-2 tabular-nums" data-testid="roi-revenue">{fmtEur.format(recovered)}</div>
          <div className="mt-8 pt-6 border-t border-white/10">
            <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">Temps administratif économisé</div>
            <div className="font-heading text-3xl font-extrabold mt-2 tabular-nums" data-testid="roi-hours">{fmt.format(hours)} h / an</div>
          </div>
          <button onClick={() => toast.success(`Potentiel estimé : ${fmtEur.format(recovered)} par an 🚀`)} data-testid="produit-roi-cta"
            className="mt-8 h-12 px-6 rounded-full bg-[#10B981] hover:bg-emerald-500 font-semibold inline-flex items-center gap-2 transition-colors">Concrétiser ce potentiel <ArrowRight className="w-4 h-4" /></button>
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section id="tarifs" className="bg-white border-t border-slate-200 scroll-mt-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A]">Des offres simples et dégressives</h2>
        <p className="text-slate-500 mt-3">Toutes les fonctionnalités incluses. Résiliable à tout moment.</p>
        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          {PLANS.map((p) => (
            <div key={p.name} data-testid={`produit-plan-${p.name.toLowerCase()}`} className={`rounded-3xl p-7 transition-transform hover:-translate-y-1 ${p.popular ? "bg-[#0F172A] text-white shadow-2xl shadow-slate-900/25" : "bg-slate-50 border border-slate-200 text-[#0F172A]"}`}>
              <div className="flex items-center justify-between"><span className="font-heading font-bold">{p.name}</span>{p.popular && <span className="text-[10px] font-bold uppercase tracking-wide bg-[#10B981] text-white rounded-full px-2 py-0.5">Le plus choisi</span>}</div>
              <div className="font-heading text-4xl font-extrabold mt-5">{p.price}</div>
              <div className={`text-xs ${p.popular ? "text-slate-400" : "text-slate-500"}`}>{p.unit} · {p.sub}</div>
              <Link to={REGISTER_URL} data-testid={`produit-plan-${p.name.toLowerCase()}-cta`} className={`mt-6 h-11 rounded-full font-semibold text-sm flex items-center justify-center transition-colors ${p.popular ? "bg-[#10B981] hover:bg-emerald-500 text-white" : "bg-white border border-slate-300 hover:bg-slate-100"}`}>Essayer 7 jours</Link>
            </div>
          ))}
        </div>
        <p className="text-sm text-slate-500 mt-6 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#10B981]" /> Essai gratuit 7 jours · sans carte bancaire</p>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="bg-[#0F172A] text-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight">Moins de paperasse.<br /><span className="text-[#10B981]">Plus de chantiers.</span></h2>
        <p className="text-slate-300 mt-5 max-w-xl mx-auto">Lancez votre essai en 2 minutes et dictez votre premier chantier dès aujourd'hui.</p>
        <div className="mt-9 flex flex-wrap gap-3 justify-center">
          <CtaButton testid="produit-final-cta">Démarrer l'essai gratuit <ArrowRight className="w-5 h-5" /></CtaButton>
          <a href={PARTNER_MAILTO} data-testid="produit-partner-cta" className="inline-flex items-center gap-2 h-14 px-7 rounded-full border border-white/20 font-semibold hover:bg-white/10 transition-colors"><Handshake className="w-4 h-4" /> Devenir partenaire</a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0B1222] text-slate-500">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
        <span>© 2026 Kallitag · Terrain</span>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-center">
          <Link to="/confidentialite" className="hover:text-white inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Confidentialité</Link>
          <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link>
          <Link to="/cgu" className="hover:text-white">CGU / CGV</Link>
          <Link to="/dpa" className="hover:text-white">DPA</Link>
          <Link to="/decouvrir" className="hover:text-white inline-flex items-center gap-1">Découvrir <ChevronRight className="w-3.5 h-3.5" /></Link>
        </div>
      </div>
    </footer>
  );
}

export default function ProduitPage() {
  return (
    <div className="min-h-screen bg-white" style={{ color: NAVY }} data-testid="produit-page">
      <Header />
      <Hero />
      <Stats />
      {FEATURES.map((f, i) => <FeatureBlock key={f.id} f={f} idx={i} />)}
      <Extras />
      <Roi />
      <Pricing />
      <FinalCta />
      <div className="h-24 sm:hidden bg-[#0B1222]" />
      <Footer />
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-50 p-3 bg-[#0F172A]/95 backdrop-blur-xl border-t border-white/10" data-testid="produit-sticky-cta">
        <Link to={REGISTER_URL} data-testid="produit-sticky-cta-link" className="w-full h-13 py-3.5 rounded-full bg-[#10B981] text-white font-bold flex items-center justify-center gap-2">Essayer gratuitement <ArrowRight className="w-5 h-5" /></Link>
      </div>
    </div>
  );
}
